import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ClockIcon,
  MapPinIcon,
  ArrowPathIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { sessionService, Session } from '../services/api.ts';
import { format } from 'date-fns';
import { TableHeaderVariants } from '../components/TableHeaderVariants.tsx';

type SortField = 'date' | 'score' | 'device' | 'location' | 'status';
type SortDirection = 'asc' | 'desc';

function History() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const sessionsPerPage = 20;
  const [allSessions, setAllSessions] = useState<Session[]>([]); // Store all sessions for filtering

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Advanced filters
  const [locationFilter, setLocationFilter] = useState<string>('');
  const [timePeriod, setTimePeriod] = useState<number>(7); // Days to look back, 0 = all sessions

  const fetchSessions = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch ALL sessions without pagination or filtering for proper client-side filtering
      const response = await sessionService.getSessions(
        undefined,
        undefined, // Don't filter by status at API level
        undefined,
        undefined,
        0,
        10000 // Large limit to get all sessions
      );

      setAllSessions(response.sessions || []);
      setSessions(response.sessions || []);
      setTotalSessions(response.total || 0);
      setCompletedCount(response.completed || 0);
      setFailedCount(response.failed || 0);
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      setError(err?.message || i18n.t('history.loadErrorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [i18n]); // Remove sessionFilter dependency since we fetch all sessions now

  useEffect(() => {
    fetchSessions();
    setLastRefresh(new Date());
  }, [fetchSessions]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchSessions();
      setLastRefresh(new Date());
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const handleRefresh = async () => {
    await fetchSessions();
    setLastRefresh(new Date());
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Sorting handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get unique locations for filters
  const uniqueLocations = useMemo(() => {
    const locations = new Set(allSessions.map(s => s.location_name).filter(Boolean));
    return Array.from(locations).sort();
  }, [allSessions]);

  // Filter and sort sessions
  const filteredAndSortedSessions = useMemo(() => {
    let filtered = [...allSessions];

    // Apply location filter
    if (locationFilter) {
      filtered = filtered.filter(s => s.location_name === locationFilter);
    }

    // Apply time period filter (0 = all sessions)
    if (timePeriod > 0) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - timePeriod);
      filtered = filtered.filter(s => new Date(s.start_time) >= cutoffDate);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'date':
          aValue = new Date(a.start_time).getTime();
          bValue = new Date(b.start_time).getTime();
          break;
        case 'score':
          aValue = a.average_score ?? -1;
          bValue = b.average_score ?? -1;
          break;
        case 'device':
          aValue = (a.device_name || '').toLowerCase();
          bValue = (b.device_name || '').toLowerCase();
          break;
        case 'location':
          aValue = (a.location_name || '').toLowerCase();
          bValue = (b.location_name || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allSessions, locationFilter, timePeriod, sortField, sortDirection]);

  // Paginated display of filtered sessions
  const paginatedSessions = useMemo(() => {
    const startIdx = (currentPage - 1) * sessionsPerPage;
    const endIdx = startIdx + sessionsPerPage;
    return filteredAndSortedSessions.slice(startIdx, endIdx);
  }, [filteredAndSortedSessions, currentPage, sessionsPerPage]);

  const totalFilteredPages = Math.ceil(filteredAndSortedSessions.length / sessionsPerPage);

  // Clear all filters
  const clearAllFilters = () => {
    setLocationFilter('');
    setTimePeriod(7); // Reset to default 7 days
    setCurrentPage(1); // Reset to first page
  };

  const hasActiveFilters = locationFilter || timePeriod !== 7;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [locationFilter, timePeriod]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'in_progress':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'failed':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  const statusLabel = (status: string) => {
    if (status === 'completed') return t('history.status.completed');
    if (status === 'in_progress') return t('history.status.in_progress');
    if (status === 'failed') return t('history.status.failed');
    return status;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold text-regina-gray-800 sm:text-2xl">{t('history.title')}</h1>
        <p className="mt-1 text-sm text-regina-gray-600">{t('history.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="flex shrink-0 items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-regina-red" />
            <span className="font-semibold text-regina-gray-800">{t('history.filters')}</span>
          </div>

          <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end md:w-auto">
            {/* Location Filter */}
            <select
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
              className="w-full min-w-0 rounded-lg border border-regina-gray-200 bg-white px-3 py-2 text-sm text-regina-gray-800 focus:outline-none focus:ring-2 focus:ring-regina-red sm:w-auto"
            >
              <option value="">{t('history.allLocations')}</option>
              {uniqueLocations.map(location => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>

            {/* Time Period Filter */}
            <div className="flex w-full flex-col gap-1 sm:w-auto sm:flex-row sm:items-center sm:space-x-2 sm:gap-0">
              <span className="text-sm text-regina-gray-600">{t('history.timePeriod')}</span>
              <select
                value={timePeriod}
                onChange={(e) => setTimePeriod(Number(e.target.value))}
                className="w-full min-w-0 rounded-lg border border-regina-gray-200 bg-white px-3 py-2 text-sm text-regina-gray-800 focus:outline-none focus:ring-2 focus:ring-regina-red sm:w-auto"
              >
                <option value={7}>{t('history.lastDays', { count: 7 })}</option>
                <option value={14}>{t('history.lastDays', { count: 14 })}</option>
                <option value={30}>{t('history.lastDays', { count: 30 })}</option>
                <option value={90}>{t('history.lastDays', { count: 90 })}</option>
                <option value={0}>{t('history.allSessions')}</option>
              </select>
            </div>

            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="flex items-center justify-center space-x-1 rounded-lg px-3 py-2 text-sm text-regina-red transition-colors hover:text-red-700 sm:justify-start"
              >
                <XMarkIcon className="h-4 w-4" />
                <span>{t('history.clearFilters')}</span>
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-regina-error mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-red-800 font-medium">{t('history.loadErrorTitle')}</div>
              <div className="text-red-700 text-sm mt-1">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions List */}
      {loading && allSessions.length === 0 ? (
        <div className="bg-white rounded-lg border border-regina-gray-100 p-8 shadow-sm">
          <div className="text-center text-regina-gray-600">{t('history.loadingSessions')}</div>
        </div>
      ) : filteredAndSortedSessions.length === 0 ? (
        <div className="bg-white rounded-lg border border-regina-gray-100 p-8 shadow-sm">
          <div className="text-center text-regina-gray-600">
            {hasActiveFilters ? t('history.emptyFiltered') : t('history.empty')}
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-regina-gray-100 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-regina-gray-200">
                <TableHeaderVariants
                  sortField={sortField}
                  sortDirection={sortDirection}
                  handleSort={handleSort}
                  variant={5}
                />
                <tbody className="divide-y divide-regina-gray-200 bg-white">
                  {paginatedSessions.map((session) => (
                    <tr
                      key={session.session_id}
                      onClick={() => navigate(`/session/${session.session_id}`)}
                      className="hover:bg-regina-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-semibold ${session.average_score ? getScoreColor(session.average_score) : 'text-regina-gray-400'}`}>
                          {session.average_score ? session.average_score.toFixed(1) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <ClockIcon className="w-4 h-4 text-regina-gray-600" />
                          <span className="text-sm text-regina-gray-800">
                            {format(new Date(session.start_time), 'dd/MM/yyyy HH:mm')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadge(session.status)}`}>
                          {statusLabel(session.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-1 text-sm text-regina-gray-700">
                          <MapPinIcon className="w-4 h-4 text-regina-gray-600" />
                          <span>{session.location_name || t('common.unknown')}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-regina-gray-800">{session.device_name || t('common.unknown')}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination and Count */}
          <div className="rounded-lg border border-regina-gray-100 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
              <div className="text-sm text-regina-gray-600">
                {t(
                  filteredAndSortedSessions.length === 1
                    ? 'history.entrySingular'
                    : 'history.entryPlural',
                  { count: filteredAndSortedSessions.length }
                )}
              </div>
              
              {totalFilteredPages > 1 && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:space-x-4 sm:gap-0">
                  <div className="text-sm text-regina-gray-600">
                    {t('history.pageOf', { current: currentPage, total: totalFilteredPages })}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage === 1
                          ? 'bg-regina-gray-100 text-regina-gray-400 cursor-not-allowed'
                          : 'bg-regina-gray-100 text-regina-gray-800 hover:bg-regina-gray-200'
                      }`}
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    
                    {/* Page Numbers */}
                    <div className="flex items-center space-x-1">
                      {[...Array(Math.min(5, totalFilteredPages))].map((_, idx) => {
                        let pageNum;
                        if (totalFilteredPages <= 5) {
                          pageNum = idx + 1;
                        } else if (currentPage <= 3) {
                          pageNum = idx + 1;
                        } else if (currentPage >= totalFilteredPages - 2) {
                          pageNum = totalFilteredPages - 4 + idx;
                        } else {
                          pageNum = currentPage - 2 + idx;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-10 h-10 rounded-lg transition-colors ${
                              currentPage === pageNum
                                ? 'bg-regina-red text-white'
                                : 'bg-regina-gray-100 text-regina-gray-700 hover:bg-regina-gray-200'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalFilteredPages}
                      className={`p-2 rounded-lg transition-colors ${
                        currentPage === totalFilteredPages
                          ? 'bg-regina-gray-100 text-regina-gray-400 cursor-not-allowed'
                          : 'bg-regina-gray-100 text-regina-gray-800 hover:bg-regina-gray-200'
                      }`}
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default History;
